import {Button, message} from "antd";
import React, {FC, memo, useEffect, useMemo, useState} from "react";
import moment from "moment";
import {
    ArrayDataOptions,
    GetPaidScheduledPaymentsQueryVariables,
    TablesEnum,
    useGetCompanyInfoQuery,
    useGetPaidScheduledPaymentsQuery,
    useUpdateManyScheduledPaymentIdsMutation,
} from "../../generated/graphql";
import i18n from "../../services/i18n";
import {rowId, useRowChangeHighlight} from "../../helpers/useRowChangeHighlight";
import Page from "../../views/Page/Page";
import PaymentsList, {allColumns} from "./PaymentsList";
import {TableType} from "./PaymentsListColumns";
import {useLocalStorageOptions} from "../../helpers/columnsLocalStorageHandler";
import ExportPaymentsModal from "./ExportPayments/ExportPaymentsModal";
import {ExportConfig} from "./ExportPayments/types";
import YearEndExportModal from "./YearEndExport/YearEndExportModal";
import {GET_PAID_PAYMENTS} from "./scheduledPayments.graphql";
import CourseListShowMoreMenu from "../courses/CourseEdit/CourseListShowMoreMenu";

type DonePaymentType = {
    hideTitle?: boolean;
    customColumns?: Array<Extract<keyof TableType, string>>;
    hidePagination?: boolean;
    tablesEnum?: TablesEnum;
    hideXmlBtn?: boolean;
    style?: React.CSSProperties;
    bookingId?: string;
    hiddenFilters?: Array<Extract<keyof TableType, string>>;
    minPadding?: boolean;
    pollInterval?: number;
    tableTitle?: string | React.ReactNode;
    hideSpecialFunctions?: boolean;
    hideFooter?: boolean;
    newTimeStamp?: number;
    timeStamp?: number;
    /** Zeitpunkt einer fremden Änderung, die der Buchungsdialog gemeldet hat (Live-Aktualisierung). */
    liveStamp?: number;
    setTimeStamp?: (newTimeStamp: number) => void;
    hideExportButton?: boolean;
};

const paymentExportConfig: ExportConfig = {
    title: "Umsätze exportieren",
    entityLabel: "Umsätze",
    dateFieldKey: "paymentDone",
    dateFieldTable: "scheduledPayment",
    dateFieldDescription: "Zeitraum bezieht sich auf: Bezahldatum",
    fileNamePrefix: "Umsaetze-Export",
    csvTablesEnum: TablesEnum.DonePaymentsList,
    exportTablesEnum: TablesEnum.DonePaymentsListExport,
    query: GET_PAID_PAYMENTS,
    getItemsFromResult: (data: any) => ({
        items: data.paidScheduledPayments?.items ?? [],
        total: data.paidScheduledPayments?.total ?? 0,
    }),
    buildVariables: (options: ArrayDataOptions) => ({options, bookingId: null}),
    sortField: "paymentDone",
};

const DonePaymentsList: FC<DonePaymentType> = ({
    hideTitle,
    customColumns,
    hidePagination,
    tablesEnum = TablesEnum.DonePaymentsList,
    hideXmlBtn,
    style,
    bookingId,
    hiddenFilters,
    minPadding,
    pollInterval,
    tableTitle,
    hideSpecialFunctions,
    hideFooter = false,
    timeStamp,
    liveStamp,
    setTimeStamp,
    hideExportButton = false,
}) => {
    const localStorageName = tablesEnum;
    const [totalResults, setTotalResults] = useState<number>(0);
    const [options, setOptions] = useLocalStorageOptions({
        localStorageName,
        defaultOptions: {},
        totalResults,
    });

    // Feature-Flag: Mollie-Spalten nur anzeigen wenn Feature aktiv
    const {data: companyInfoData} = useGetCompanyInfoQuery();
    const featureMollie = !!companyInfoData?.companyInfo?.subscription?.featureMollie;

    // Phase 14 / Plan 14-05 (D-23) — refundStatus ist ein Mollie-Column, nur sichtbar
    // wenn featureMollie aktiv ist. Bei Deaktivierung verschwindet die Spalte analog
    // paymentSource (bestehendes Pattern).
    const MOLLIE_COLUMNS: Array<Extract<keyof TableType, string>> = ["paymentSource", "refundStatus"];

    const allDefaultColumns: Array<Extract<keyof TableType, string>> = [
        "bookingNumber",
        "courseType",
        "courseNumber",
        "booker",
        "attendee",
        "location",
        "firstCourseLesson",
        "invoice",
        "paymentMethod",
        "paymentSource",
        "paymentDone",
        "paymentAmount",
        "refundStatus",
        // B-223 Followup — "Aktiv"/"Gelöscht"-Tag inkl. Restore-Popover.
        "tombstoneStatus",
        "setUnPaidAction",
    ];

    const defaultColumns = useMemo(() => (featureMollie ? allDefaultColumns : allDefaultColumns.filter((col) => MOLLIE_COLUMNS.indexOf(col) === -1)), [featureMollie]);

    // GET DATA FOR LIST --------------------------------------------------------------------------------------------------------------------
    const {data, loading, refetch} = useGetPaidScheduledPaymentsQuery({
        variables: {
            options: options as GetPaidScheduledPaymentsQueryVariables["options"],
            bookingId,
            // includeDeleted: showDeleted,
            // Archiv-Einstellung des Logins anwenden; der Export fragt ohne und bleibt vollständig
            respectArchiveSetting: true,
        },
        fetchPolicy: "network-only",
        pollInterval,
    });

    // Live-Aktualisierung nur im Buchungsdialog (eine Buchung, kleine Liste), ausgelöst über `liveStamp`.
    const liveRows = useMemo(() => data?.paidScheduledPayments?.items ?? [], [data]);
    const highlight = useRowChangeHighlight<{id: string}>(liveRows, rowId, JSON.stringify([options, bookingId]), data?.paidScheduledPayments?.total);

    React.useEffect(() => {
        refetch();
    }, [timeStamp, refetch]);

    // Fremde Änderung im Buchungsdialog (PanelCardPayments): nachladen und Geändertes markieren.
    React.useEffect(() => {
        if (liveStamp) highlight.track(refetch, true).catch(() => undefined);
    }, [liveStamp, highlight.track, refetch]);

    const handleRefetch = () => {
        setTimeStamp?.(moment().unix());
        refetch();
    };

    useEffect(() => {
        totalResults !== data?.paidScheduledPayments?.total && setTotalResults(data?.paidScheduledPayments?.total ?? 0);
    }, [data, totalResults]);

    // useEffect(() => {
    //     refetch();
    // }, [refetch, tableTitle, timeStamp]);

    const tableData = {
        dataSource: (data?.paidScheduledPayments?.items ?? []) as unknown as TableType[],
        existMore: data?.paidScheduledPayments?.existsMore ?? false,
        total: data?.paidScheduledPayments?.total ?? 0,
        loading,
    };

    // MARK SELECTION OF PAYMENTS ----------------------------------------------------

    const [selection, setSelection] = useState<Array<string>>([]);

    const [updateManyScheduledPaymentMutation] = useUpdateManyScheduledPaymentIdsMutation();

    const setAsUnPaid = async () => {
        const hide = message.loading(i18n.containers.payments.DonePaymentsList.operate());

        try {
            await updateManyScheduledPaymentMutation({
                variables: {
                    paymentIds: selection,
                    editScheduledPaymentData: {
                        paymentDone: null,
                    },
                },
            });
            // Erfolgsmeldung, Selektion leeren und Refetch NUR bei Erfolg
            message.success(i18n.containers.payments.DonePaymentsList.markedAsUnPaidFinished(), 2);
            setSelection([]);
            handleRefetch();
        } catch (error) {
            message.error(error instanceof Error ? error.message : String(error), 3);
        } finally {
            hide();
        }
    };

    const onOk = () => {
        setAsUnPaid();
    };

    const MarkSelection = () => {
        return (
            <Button type="primary" size="small" onClick={() => onOk()}>
                {i18n.containers.payments.DonePaymentsList.markItemsAsUnPaid()}
            </Button>
        );
    };

    const hiddenColumns: Array<Extract<keyof TableType, string>> = ["id", "setPaidAction", "setUnPaidAction"];

    return (
        <Page
            data-testid="DonePaymentsList"
            content={
                <PaymentsList
                    tableTitle={tableTitle ?? i18n.containers.payments.DonePaymentsList.tableTitle()}
                    titleHint={highlight.goneCount > 0 && <span className={highlight.goneHintClassName}>{i18n.containers.payments.DonePaymentsList.liveGone({count: String(highlight.goneCount)})}</span>}
                    rowClassName={highlight.rowClassName}
                    defaultColumns={customColumns ?? defaultColumns}
                    // initialColumns={columns}
                    tableData={tableData}
                    setOptions={setOptions}
                    options={options}
                    refetch={handleRefetch}
                    // optionsCallBack={(
                    //     options: React.SetStateAction<SortFilterOptionsType>,
                    // ) => setOptions(options)}
                    specialFunctions={hideSpecialFunctions ? undefined : [<MarkSelection key="markSelectionUnpaid" />]}
                    selection={(selectedItems: React.SetStateAction<Array<string>>) => setSelection(selectedItems)}
                    tablesEnum={tablesEnum}
                    hidePagination={hidePagination}
                    style={style}
                    hiddenFilters={hiddenFilters}
                    minPadding={minPadding}
                    contentType="donePayment"
                    hideFooter={hideFooter}
                    extraFunctions={
                        [
                            !bookingId && <CourseListShowMoreMenu key="archive-menu" refetch={handleRefetch} />,
                            !hideExportButton && (
                                <ExportPaymentsModal
                                    key="export-payments"
                                    options={options}
                                    columnsToShow={customColumns ?? defaultColumns}
                                    allColumns={allColumns()}
                                    hiddenColumns={hiddenColumns}
                                    mandatoryColumns={["paymentDone", "paymentAmount"]}
                                    exportConfig={paymentExportConfig}
                                />
                            ),
                            // Nur auf der Umsätze-Liste, nicht im eingebetteten Buchungs-Panel;
                            // rendert sich zusätzlich nur für KO-Superuser (siehe YearEndExportModal).
                            !hideExportButton && <YearEndExportModal key="year-end-export" />,
                        ].filter(Boolean) as React.ReactNode[]
                    }
                />
            }
        />
    );
};

export default memo(DonePaymentsList);
